import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import LinkIcon from '@mui/icons-material/Link';
import { extractYoutubeVideoId } from '../../utils/youtubeUtils';
import useCustomFetch from '../../hooks/useCustomFetch';
import { API_ENDPOINTS } from '../../config/api';
import customFetch from '../../utils/axios';

const MUSICAL_KEYS = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

const CustomDialogContent = styled(DialogContent)(({ theme }) => ({
  overflowY: 'auto', // Default for mobile
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.primary.main} #f1f1f1`,
  
  // Desktop styles
  [theme.breakpoints.up('sm')]: {
    overflowY: 'scroll', // Always show scrollbar on desktop
  },
  
  '&::-webkit-scrollbar': {
    width: '12px',
    backgroundColor: '#f1f1f1',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: '#f1f1f1',
    borderRadius: '8px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.primary.main,
    borderRadius: '8px',
    border: '3px solid #f1f1f1',
    minHeight: '40px',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}));

const SongForm = ({ open, onClose, onSubmit, song }) => {
  const { t } = useTranslation();
  const { get } = useCustomFetch();
  const [formData, setFormData] = useState({
    title: '',
    defaultKey: '',
    author: '',
    ccli: '',
    streamingLinks: [],
    chordChart: {
      content: '',
      format: 'text'
    },
    defaultArrangement: '',
    tempo: '',
    timeSignature: '',
    notes: ''
  });

  const [streamingLinks, setStreamingLinks] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (song) {
      // Ensure chordChart has the correct structure
      const chordChart = typeof song.chordChart === 'string'
        ? { content: song.chordChart, format: 'text' }
        : song.chordChart || { content: '', format: 'text' };

      const initialData = {
        title: song.title || '',
        defaultKey: song.defaultKey || '',
        author: song.author || '',
        copyright: song.copyright || '',
        ccli: song.ccli || '',
        streamingLinks: song.streamingLinks || [],
        defaultArrangement: song.defaultArrangement || '',
        tempo: song.tempo || '',
        timeSignature: song.timeSignature || '',
        notes: song.notes || '',
        tags: song.tags || [],
        chordChart
      };

      setFormData(initialData);
      setStreamingLinks(song.streamingLinks || []);
    }
  }, [song]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested fields (e.g., chordChart.content)
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setFormErrors({});

    // Create a copy of formData to clean up before submission
    const submissionData = { ...formData };

    // Convert tempo to number or null if empty
    submissionData.tempo = submissionData.tempo ? Number(submissionData.tempo) : null;

    try {
      await onSubmit(submissionData);
      onClose();
    } catch (error) {
      console.error('Error submitting song:', error);
      setFormErrors({
        submit: error.message || t('songs.form.error')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name.startsWith('streamingLinks.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else if (name.startsWith('chordChart.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleAddStreamingLink = () => {
    setFormData(prev => ({
      ...prev,
      streamingLinks: [
        ...prev.streamingLinks,
        { type: 'youtube', url: '', title: '' }
      ]
    }));
  };

  const handleStreamingLinkChange = (index, field, value) => {
    setFormData(prev => {
      const newLinks = [...prev.streamingLinks];
      newLinks[index] = {
        ...newLinks[index],
        [field]: value
      };
      return {
        ...prev,
        streamingLinks: newLinks
      };
    });
  };

  const handleRemoveStreamingLink = (index) => {
    setFormData(prev => ({
      ...prev,
      streamingLinks: prev.streamingLinks.filter((_, i) => i !== index)
    }));
  };

  // Handle streaming link change with YouTube title fetching
  const handleStreamingLinkChangeWithFetch = async (index, field, value) => {
    const newLinks = [...formData.streamingLinks];
    newLinks[index] = {
      ...newLinks[index],
      [field]: value
    };

    // If this is a URL change and it's a YouTube URL, try to fetch the title
    if (field === 'url' && value) {
      try {
        const videoId = extractYoutubeVideoId(value);
        if (videoId) {
          newLinks[index].type = 'youtube';
          
          // Try to fetch video details
          try {
            const response = await customFetch.get(API_ENDPOINTS.YOUTUBE.VIDEO_DETAILS(videoId));
            if (response?.data?.title) {
              newLinks[index].title = response.data.title;
            }
          } catch (error) {
            console.warn('Could not fetch video title:', error);
            // Set a default title if fetch fails
            if (!newLinks[index].title) {
              newLinks[index].title = t('songs.form.youtubeVideo');
            }
          }
        }
      } catch (error) {
        console.error('Error processing video URL:', error);
      }
    }

    setFormData(prev => ({
      ...prev,
      streamingLinks: newLinks
    }));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!['audio/mpeg', 'audio/wav'].includes(file.type)) {
      alert(t('songs.form.invalidFileType'));
      return;
    }

    // Validate file size (107MB)
    if (file.size > 107 * 1024 * 1024) {
      alert(t('songs.form.fileSizeLimit'));
      return;
    }

    const formData = new FormData();
    formData.append('audio', file);
    formData.append('title', file.name);

    try {
      // Use song._id if editing, or wait for response if creating new song
      const songIdToUse = song?._id;
      
      if (!songIdToUse) {
        alert(t('songs.form.saveFirst'));
        return;
      }

      const response = await customFetch.post(
        API_ENDPOINTS.SONGS.UPLOAD_AUDIO(songIdToUse),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const progress = (progressEvent.loaded / progressEvent.total) * 100;
            setUploadProgress(progress);
          },
        }
      );

      setFormData(prev => ({
        ...prev,
        streamingLinks: [
          ...prev.streamingLinks,
          response.data
        ]
      }));
      setUploadProgress(0);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(t('songs.form.uploadError'));
      setUploadProgress(0);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
        sx: {
          height: '100%',
          maxHeight: { xs: '100%', sm: 'calc(100% - 64px)' }
        }
      }}
    >
      <DialogTitle sx={{
        p: 2,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography variant="h6" component="div">
          {song ? t('songs.editSong') : t('songs.addSong')}
        </Typography>
        <IconButton
          aria-label={t('common.close')}
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <CustomDialogContent sx={{ p: 2, pb: 0 }}>
        <Grid container spacing={2}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              {t('songs.form.basicInfo')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              fullWidth
              name="title"
              label={t('songs.form.title')}
              value={formData.title}
              onChange={handleInputChange}
              error={!!formErrors.title}
              helperText={formErrors.title}
              disabled={isSubmitting}
              placeholder={t('songs.form.titlePlaceholder')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required error={!!formErrors.defaultKey}>
              <InputLabel id="key-label">{t('songs.form.key')}</InputLabel>
              <Select
                labelId="key-label"
                name="defaultKey"
                value={formData.defaultKey}
                onChange={handleInputChange}
                label={t('songs.form.key')}
                disabled={isSubmitting}
              >
                {MUSICAL_KEYS.map((key) => (
                  <MenuItem key={key} value={key}>{key}</MenuItem>
                ))}
              </Select>
              {formErrors.defaultKey && (
                <Typography variant="caption" color="error">
                  {formErrors.defaultKey}
                </Typography>
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="author"
              label={t('songs.form.author')}
              value={formData.author}
              onChange={handleInputChange}
              disabled={isSubmitting}
              placeholder={t('songs.form.authorPlaceholder')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="ccli"
              label={t('songs.form.ccli')}
              value={formData.ccli}
              onChange={handleInputChange}
              disabled={isSubmitting}
              placeholder={t('songs.form.ccliPlaceholder')}
            />
          </Grid>

          {/* Streaming Links */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
              {t('songs.form.streamingLinks')}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {formData.streamingLinks.map((link, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: 2,
                  mb: 2,
                  flexDirection: { xs: 'column', sm: 'row' }
                }}
              >
                <FormControl sx={{ minWidth: { xs: '100%', sm: 120 } }}>
                  <InputLabel>{t('songs.form.type')}</InputLabel>
                  <Select
                    value={link.type}
                    onChange={(e) => handleStreamingLinkChange(index, 'type', e.target.value)}
                    label={t('songs.form.type')}
                  >
                    <MenuItem value="youtube">{t('songs.form.youtube')}</MenuItem>
                    <MenuItem value="spotify">{t('songs.form.spotify')}</MenuItem>
                    <MenuItem value="other">{t('songs.form.other')}</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  required
                  label={t('songs.form.url')}
                  value={link.url}
                  onChange={(e) => handleStreamingLinkChangeWithFetch(index, 'url', e.target.value)}
                  sx={{ flex: { xs: 1, sm: 2 } }}
                  placeholder={t('songs.form.urlPlaceholder')}
                />
                <TextField
                  fullWidth
                  label={t('songs.form.title')}
                  value={link.title || ''}
                  onChange={(e) => handleStreamingLinkChange(index, 'title', e.target.value)}
                  sx={{ flex: { xs: 1, sm: 2 } }}
                  placeholder={t('songs.form.titlePlaceholder')}
                />
                <IconButton
                  onClick={() => handleRemoveStreamingLink(index)}
                  sx={{ alignSelf: 'center', color: '#ff5252' }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
              <Button
                onClick={handleAddStreamingLink}
                startIcon={<AddIcon />}
                variant="outlined"
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                {t('songs.form.addStreamingLink')}
              </Button>
            </Box>
          </Grid>

          {/* Chord Chart */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              {t('songs.form.chordChart')}
            </Typography>
            <TextField
              name="chordChart.content"
              value={formData.chordChart.content}
              onChange={handleInputChange}
              multiline
              rows={8}
              fullWidth
              placeholder={t('songs.form.chordChartPlaceholder')}
              variant="outlined"
              InputProps={{
                style: { fontFamily: 'monospace' }
              }}
            />
          </Grid>

          {/* Additional Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
              {t('songs.form.additionalInfo')}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="defaultArrangement"
              label={t('songs.form.defaultArrangement')}
              value={formData.defaultArrangement}
              onChange={handleInputChange}
              placeholder={t('songs.form.defaultArrangementPlaceholder')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="tempo"
              label={t('songs.form.tempo')}
              type="number"
              value={formData.tempo}
              onChange={handleInputChange}
              placeholder={t('songs.form.tempoPlaceholder')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="timeSignature"
              label={t('songs.form.timeSignature')}
              value={formData.timeSignature}
              onChange={handleInputChange}
              placeholder={t('songs.form.timeSignaturePlaceholder')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              name="notes"
              label={t('songs.form.notes')}
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleInputChange}
              placeholder={t('songs.form.notesPlaceholder')}
            />
          </Grid>
        </Grid>
      </CustomDialogContent>

      <DialogActions sx={{ p: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} color="inherit">
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
          {song ? t('songs.form.saveChanges') : t('songs.form.createSong')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SongForm;
